/**
 * Bulk-check hook — persistent checkbox state for the "bulk availability
 * check" toggle that appears next to the service dropdowns when the
 * currently selected favorite is SecureMyName (and, later, other
 * registrars that expose a bulk-availability endpoint).
 *
 * The checkbox itself only flips a stored flag today; the underlying
 * bulk lookup wiring lands in a follow-up commit once the SMN syntax is
 * shared. Persisting now means users who opt in once stay opted in
 * across sessions, including PWA relaunches.
 *
 * Storage key: aiprompt:bulk-check:v1 ('on' | 'off')
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'aiprompt:bulk-check:v1';

function readInitial(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

export function useBulkCheck() {
  const [bulkCheckEnabled, setBulkCheckEnabled] = useState<boolean>(() =>
    readInitial(),
  );

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, bulkCheckEnabled ? 'on' : 'off');
    } catch {
      /* localStorage unavailable; ignore */
    }
  }, [bulkCheckEnabled]);

  const toggle = useCallback(
    () => setBulkCheckEnabled((prev) => !prev),
    [],
  );

  return { bulkCheckEnabled, setBulkCheckEnabled, toggleBulkCheck: toggle };
}
